<template>
  <ContextColorProvider context="wealth">
    <NavBar>
      <template #left>
        <BackButton @click="handleGoBackClick" />
      </template>
      <template #right>
        <ShowMoneyToggleButton />
      </template>
    </NavBar>
    <main class="performance__main">
      <HelperViewStatus
        v-if="shouldRenderGlobalErrorState"
        class="performance__main__error"
        title="Não foi possível carregar a performance"
        description="Aguarde um momento e tente novamente"
        button-label="Tentar novamente"
        status="alert"
        @click="getPerformanceData"
      />
      <template v-else>
        <PerformanceHeader
          class="performance__main__header"
          :performance-data="performanceData"
          :status="status"
          :selected-year="selectedYear"
          @on-change-year="handleChangeYear"
        />
        <PerformanceTable
          class="performance__main__table"
          :performance-data="performanceData"
          :status="status"
          @on-retry="getPerformanceData"
        />
      </template>
    </main>
  </ContextColorProvider>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';

import {
  NavBar,
  ContextColorProvider,
  HelperViewStatus
} from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons';
import { ShowMoneyToggleButton } from '@/modules/common/components/buttons/ShowMoneyToggleButton';
import { PerformanceHeader } from './components/PerformanceHeader';
import { PerformanceTable } from './components/PerformanceTable';

import {
  getPortfolioPerformance,
  PortfolioPerformanceFilters,
  PortfolioPerformanceResponse
} from '@/modules/wealth/services/portfolio';
import { PerformanceFetchingStatus } from './types';

let abortSignal: AbortController | null = null;

const performanceData = ref<PortfolioPerformanceResponse | null>(null);
const selectedYear = ref<string | number | null>(null);
const status = ref<PerformanceFetchingStatus>(
  PerformanceFetchingStatus.Default
);

const route = useRoute();
const router = useRouter();

const portfolioId = computed(() => {
  const { portfolioId } = route.params;
  return portfolioId;
});

const shouldRenderGlobalErrorState = computed(() => {
  return (
    status.value === PerformanceFetchingStatus.Error && !performanceData.value
  );
});

const handleGoBackClick = () => {
  router.replace({
    name: 'wealthIndexPortfolio',
    params: { portfolioId: portfolioId.value }
  });
};

const getPerformanceData = async () => {
  try {
    if (abortSignal) abortSignal.abort();
    abortSignal = new AbortController();

    status.value = PerformanceFetchingStatus.Loading;

    const params = {
      portfolioId: portfolioId.value,
      year: selectedYear.value
    };

    const data = await getPortfolioPerformance(params, abortSignal.signal);

    performanceData.value = data;
    setSelectedYear(data.filters);

    status.value = PerformanceFetchingStatus.Default;
  } catch {
    status.value = PerformanceFetchingStatus.Error;
  }
};

const setSelectedYear = (filters: PortfolioPerformanceFilters) => {
  const selectedYearOption = filters.years.find(option => option.active);
  selectedYear.value = selectedYearOption?.value || null;
};

const handleChangeYear = async (value: string | number) => {
  selectedYear.value = value;
  await getPerformanceData();
};

onMounted(async () => {
  await getPerformanceData();
});
</script>

<style lang="less" scoped>
.performance {
  &__main {
    max-width: calc(1216px + 2 * @size-spacing-x400);
    width: 100%;
    margin: 0 auto;
    padding: @size-spacing-x600 @size-spacing-x400;

    .from-md({
      max-width: calc(1216px + 2 * @size-spacing-x800);
      padding: @size-spacing-x800 @size-spacing-x800;
    });

    &__error {
      margin: @size-spacing-x1800 auto 0 auto;
    }

    &__table {
      margin-top: @size-spacing-x500;
    }
  }
}
</style>
